import { useEffect, useRef, useState } from "react";
import { parseISO, isValid } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../../lib/utils";

/**
 * Italian date picker (hybrid): keyboard typing + calendar popover.
 * - value / onChange are ISO "YYYY-MM-DD" strings (matches native input[type=date])
 * - Users can type the date directly in dd/mm/yyyy format (auto-slashes as you type).
 * - Empty string clears the value; incomplete/invalid input holds a local text state
 *   until the field is either completed or blurred.
 */

function isoToDdmmyyyy(iso) {
  if (!iso) return "";
  const d = parseISO(iso);
  if (!isValid(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function ddmmyyyyToIso(text) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  if (yyyy < 1900 || yyyy > 2100) return null;
  const d = new Date(yyyy, mm - 1, dd);
  // Guard against overflow (e.g. 31/02 → 03/03)
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) {
    return null;
  }
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

// Insert slashes progressively as user types digits (max 8 digits: DDMMYYYY).
function autoFormat(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "gg/mm/aaaa",
  disabled,
  className,
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => isoToDdmmyyyy(value));
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef(null);

  // Sync external value → local text (e.g. when parent resets or picks via calendar)
  useEffect(() => {
    setText(isoToDdmmyyyy(value));
    setInvalid(false);
  }, [value]);

  const date = value && isValid(parseISO(value)) ? parseISO(value) : null;

  const handleTextChange = (e) => {
    const formatted = autoFormat(e.target.value);
    setText(formatted);

    if (!formatted) {
      setInvalid(false);
      if (value) onChange("");
      return;
    }
    const iso = ddmmyyyyToIso(formatted);
    if (iso) {
      setInvalid(false);
      if (iso !== value) onChange(iso);
    } else if (formatted.length === 10) {
      // Complete but invalid (e.g. 31/02/2025)
      setInvalid(true);
    } else {
      // Still typing
      setInvalid(false);
    }
  };

  const handleBlur = () => {
    if (!text) return;
    const iso = ddmmyyyyToIso(text);
    if (!iso) {
      // Revert to last valid value on blur, or clear
      setText(isoToDdmmyyyy(value));
      setInvalid(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const handleCalendarSelect = (d) => {
    if (!d) {
      onChange("");
      setText("");
      setOpen(false);
      return;
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const iso = `${yyyy}-${mm}-${dd}`;
    onChange(iso);
    setText(`${dd}/${mm}/${yyyy}`);
    setInvalid(false);
    setOpen(false);
  };

  return (
    <div
      className={cn(
        "flex h-10 w-full items-center rounded-md border bg-white text-sm",
        "focus-within:ring-2 focus-within:ring-ring ring-offset-background",
        invalid ? "border-red-400" : "border-input",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        onChange={handleTextChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        aria-invalid={invalid || undefined}
        aria-label={placeholder}
        className={cn(
          "flex-1 min-w-0 bg-transparent px-3 py-2 outline-none placeholder:text-slate-400",
          "disabled:cursor-not-allowed",
        )}
      />
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
            className="h-full aspect-square flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Apri calendario"
            data-testid={testId ? `${testId}-calendar-btn` : undefined}
          >
            <CalendarIcon size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 z-[100]"
          align="end"
          sideOffset={4}
          style={{ pointerEvents: "auto" }}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            const target = e.target;
            if (
              target &&
              target.closest &&
              target.closest("[data-radix-popper-content-wrapper]")
            ) {
              e.preventDefault();
            }
          }}
        >
          <Calendar
            mode="single"
            selected={date || undefined}
            onSelect={handleCalendarSelect}
            locale={it}
            defaultMonth={date || undefined}
            initialFocus={false}
            weekStartsOn={1}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
